import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';
import { Box, Button, IconButton } from '@mui/material';
import { RefObject, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { RemoveItemFromOrder } from 'features/RemoveItemFromOrder';
import { RemoveItemFromOrderRef } from 'features/RemoveItemFromOrder/ui/RemoveItemFromOrder/RemoveItemFromOrder';

import { ChangeCatalogItemCategoryDialog } from './ChangeCatalogItemCategoryDialog';

const ORDER_ITEM_REMOVE_BUTTON_DATA_TEST_CLASS = 'order-item-remove-button';

interface OrderGroupedItemsTableRowActionsProps {
    itemId: string;
    catalogItemId: string;
    itemName: string;
    categoryId: string;
    removeItemRef: RefObject<RemoveItemFromOrderRef>;
    canEditCatalog: boolean;
    canDelete: boolean;
    isUncategorized: boolean;
    isOrderSubmissionLocked: boolean;
}

export const OrderGroupedItemsTableRowActions = ({
    itemId,
    catalogItemId,
    itemName,
    categoryId,
    removeItemRef,
    canEditCatalog,
    canDelete,
    isUncategorized,
    isOrderSubmissionLocked,
}: OrderGroupedItemsTableRowActionsProps) => {
    const { t } = useTranslation();
    const [dialogOpen, setDialogOpen] = useState(false);

    return (
        <Box
            sx={{
                marginLeft: 'auto',
                marginTop: '-9px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
            }}
        >
            {canEditCatalog && (
                <Button
                    data-testclass="order-item-change-category-button"
                    variant="text"
                    startIcon={
                        isUncategorized ? (
                            <CreateNewFolderIcon sx={{ fontSize: '20px' }} />
                        ) : (
                            <DriveFileMoveIcon sx={{ fontSize: '20px' }} />
                        )
                    }
                    sx={{
                        color: '#3F6EB7',
                        fontSize: '13px',
                        fontWeight: 500,
                        padding: '6px 10px',
                        borderRadius: '4px',
                        textTransform: 'none',
                        minWidth: 'auto',
                        '&:hover': {
                            backgroundColor: '#E8EEF6',
                        },
                    }}
                    onClick={() => setDialogOpen(true)}
                >
                    {isUncategorized ? t('Add category') : t('Edit category')}
                </Button>
            )}
            {canDelete && (
                <RemoveItemFromOrder
                    ref={removeItemRef}
                    itemId={itemId}
                    showConfirmationDialog={false}
                    renderButton={(onClick) => (
                        <IconButton
                            disabled={isOrderSubmissionLocked}
                            sx={{
                                color: 'rgba(0, 0, 0, 0.3)',
                                padding: '7px',
                                borderRadius: '4px',
                                '&:hover': {
                                    backgroundColor: 'rgba(0, 0, 0, 0.06)',
                                },
                                '&.Mui-disabled': {
                                    opacity: 0.8,
                                    color: 'rgba(0, 0, 0, 0.3)',
                                    cursor: 'not-allowed',
                                },
                            }}
                            data-testclass={
                                ORDER_ITEM_REMOVE_BUTTON_DATA_TEST_CLASS
                            }
                            onClick={onClick}
                        >
                            <DeleteOutlineIcon sx={{ fontSize: '20px' }} />
                        </IconButton>
                    )}
                />
            )}
            {dialogOpen && (
                <ChangeCatalogItemCategoryDialog
                    itemId={catalogItemId}
                    itemName={itemName}
                    categoryId={categoryId}
                    isUncategorized={isUncategorized}
                    onClose={() => setDialogOpen(false)}
                />
            )}
        </Box>
    );
};
